// <moneybird-ascii-logo>: animated ASCII rendering of the logo mark inside a
// softly drifting character field. Self-contained (shadow DOM, own styles), so
// it works on any page that loads this bundle entrypoint via the render
// partial "ascii_logo_component".
//
// Attributes:
//   src    logo/mark SVG or PNG (required)
//   scale  mark width as a fraction of the field (smaller = smaller mark), default 0.3
//   scale-mobile  same but for narrow screens (<768px); omitted = same as scale
//   flow   "down" = continuous downward scroll (seamless loop), otherwise a gentle bob
//   cols   fixed resolution in cells; omitted = responsive based on width
//   cols-mobile  lower resolution for narrow screens (<768px)
//   fade   depth of the top/bottom opacity fade in %, default 33
//   fps    flip-book speed, default 10
//   accent accent color, default #0073E6
//   glow   "false" turns off the text-shadow glow
//   theme  "light" or "dark" (only affects glow strength), default dark
//   ramp   character set, default " .-:=+X"
//
// The element fills its box: give it a height (or an aspect-ratio) in css. The
// field is as many rows tall as fit, so nothing shifts while the art loads.

// ---- shared noise/util math (zelfde basis als de playground) ----
function hash(x, y) { const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return n - Math.floor(n); }
function smoothstep(a, b, x) { let t = (x - a) / (b - a); if (t < 0) t = 0; if (t > 1) t = 1; return t * t * (3 - 2 * t); }
function vnoise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  const a = hash(xi, yi), b = hash(xi + 1, yi), c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
  return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
}
function fbm(x, y) { let s = 0, amp = 0.5, f = 1; for (let o = 0; o < 3; o++) { s += amp * vnoise(x * f, y * f); f *= 2; amp *= 0.5; } return s / 0.875; }
function charForRamp(intensity, r) {
  if (intensity <= 0.02) return " ";
  let idx = Math.round(intensity * (r.length - 1));
  if (idx < 0) idx = 0; if (idx > r.length - 1) idx = r.length - 1;
  return r[idx];
}

const CHAR_ASPECT = 0.6; // monospace cell w/h ratio (matches line-height:1.0 below)

// Sample the logo into a luminance mask: a small mark centered in a field of
// cols x rows cells, leaving empty rows above/below for the animated ascii.
//   cols  = field width in cells (resolution)
//   rows  = field height in cells (as many as fit the box)
//   scale = mark width as a fraction of the field width (smaller = smaller mark)
function buildMask(img, cols, rows, scale) {
  const aspect = (img.naturalHeight / img.naturalWidth) || 1;
  const w = Math.max(8, Math.round(cols));
  const h = Math.max(8, Math.round(rows));
  const logoW = Math.max(4, Math.round(w * scale));
  const logoH = logoW * aspect * CHAR_ASPECT;                       // mark height in cells
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, w, h);
  ctx.drawImage(img, (w - logoW) / 2, (h - logoH) / 2, logoW, logoH); // centered, padded
  const d = ctx.getImageData(0, 0, w, h).data;
  const dark = new Float32Array(w * h);
  for (let i = 0, p = 0; i < d.length; i += 4, p++) {
    const a = d[i + 3] / 255;
    const r = d[i] * a + 255 * (1 - a), g = d[i + 1] * a + 255 * (1 - a), b = d[i + 2] * a + 255 * (1 - a);
    dark[p] = 1 - (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }
  return { w: w, h: h, dark: dark };
}

// Vertical noise offset for a cell.
//   flow="down": continuous downward scroll that still loops seamlessly, via a
//     crossfade between two noise copies one loop-length apart (frame(1) == frame(0)).
//   otherwise: a gentle up/down bob (oy = 2.2*cos).
function fieldNoise(x, y, theta, flow) {
  if (flow === "down") {
    const p = theta / (2 * Math.PI);              // 0..1 over the loop
    const L = 4.0;                                 // scroll distance per loop (noise units)
    const nA = fbm(x * 0.12, y * 0.18 - p * L);    // both layers only move down (offset shrinks)
    const nB = fbm(x * 0.12, y * 0.18 - p * L + L);
    return nA * (1 - p) + nB * p;                  // crossfade hides the seam
  }
  return fbm(x * 0.12, y * 0.18 + 2.2 * Math.cos(theta));
}
// Vertical-only baked morph (ox = 0). formed=true -> single static frame (reduced motion).
function morphFrame(m, r, theta, formed, flow) {
  const reveal = formed ? 1 : 0.5 - 0.5 * Math.cos(theta);
  const th = formed ? 0 : theta;
  const zone = m.h * 0.28;                       // top/bottom fade depth in rows
  const rows = [];
  for (let y = 0; y < m.h; y++) {
    // edge envelope: 0 at the very top/bottom, 1 in the middle -> chars fade in
    // as they enter at the top and fade out as they leave at the bottom.
    const fy = Math.min(smoothstep(0, zone, y), smoothstep(0, zone, m.h - 1 - y));
    let line = "";
    for (let x = 0; x < m.w; x++) {
      const i = y * m.w + x, base = m.dark[i];
      const n = fieldNoise(x, y, th, flow);
      let inten;
      if (base > 0.05) {
        const appear = (0.18 + 0.62 * n) * (1 - base * 0.4);
        inten = base * (0.5 + 0.5 * n) * smoothstep(appear - 0.12, appear + 0.12, reveal);
      } else {
        // drifting random ascii field filling the space around the mark
        const dens = n - 0.58;
        if (dens > 0) {
          const sh = formed ? 0.7 : 0.55 + 0.45 * Math.sin(theta * 2 + hash(x, y) * 6.283); // per-cell flicker over the loop
          inten = Math.min(0.32, dens * 1.1) * sh;
        } else inten = 0;
      }
      line += charForRamp(inten * fy, r);
    }
    rows.push(line);
  }
  return rows.join("\n");
}
function bakeFrames(m, r, N, flow) {
  const frames = [];
  for (let k = 0; k < N; k++) frames.push(morphFrame(m, r, 2 * Math.PI * k / N, false, flow));
  return frames;
}

const reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

class MoneybirdAsciiLogo extends HTMLElement {
  connectedCallback() {
    this._fps = +this.getAttribute("fps") || 10;
    this._scale = +this.getAttribute("scale") || 0.3;  // mark width as fraction of field
    this._scaleMobile = +this.getAttribute("scale-mobile") || this._scale; // larger mark on narrow screens
    this._flow = this.getAttribute("flow") || "bob";   // "down" = one-directional downward scroll
    this._accent = this.getAttribute("accent") || "#0073E6";
    this._ramp = this.getAttribute("ramp") || " .-:=+X";
    this._fixedCols = this.hasAttribute("cols") ? +this.getAttribute("cols") : null;
    this._fixedColsMobile = this.hasAttribute("cols-mobile") ? +this.getAttribute("cols-mobile") : null; // lower res on narrow screens
    this._theme = this.getAttribute("theme") || "dark";
    this._glow = this.getAttribute("glow") !== "false";
    this._fade = this.hasAttribute("fade") ? +this.getAttribute("fade") : 33; // edge-fade depth in %
    this._src = this.getAttribute("src");

    // top/bottom opacity fade; shallower fade for low bands so the mark stays solid
    const f1 = this._fade, f2 = 100 - this._fade;
    const maskGrad = "linear-gradient(to bottom,transparent 0,#000 " + f1 + "%,#000 " + f2 + "%,transparent 100%)";
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML =
      '<style>' +
      ':host{display:block}' +
      '.wrap{display:flex;align-items:center;justify-content:center;height:100%}' +
      'pre{margin:0;line-height:1.0;white-space:pre;font-variant-ligatures:none;user-select:none;' +
      'font-family:ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace;transition:color .3s;' +
      '-webkit-mask-image:' + maskGrad + ';mask-image:' + maskGrad + '}' +
      '</style>' +
      '<div class="wrap"><pre part="art" aria-hidden="true"></pre></div>';
    this._pre = root.querySelector("pre");
    this._applyStyle();

    this._mask = null; this._frames = null; this._idx = 0; this._last = 0; this._running = false;
    this._raf = this._loop.bind(this);

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => { this._img = img; this._observe(); };
    img.src = this._src;

    this._onResize = this._debounce(() => { if (this._mask) this._rebuild(); }, 200);
    window.addEventListener("resize", this._onResize);
    document.addEventListener("visibilitychange", this._onVis = () => {
      if (document.visibilityState === "hidden") this._stop();
      else if (this._inView) this._start();
    });
  }
  disconnectedCallback() {
    this._stop();
    window.removeEventListener("resize", this._onResize);
    document.removeEventListener("visibilitychange", this._onVis);
    if (this._io) this._io.disconnect();
  }

  _debounce(fn, ms) { let t; return function () { clearTimeout(t); t = setTimeout(fn, ms); }; }

  _cols() {
    if (window.innerWidth < 768 && this._fixedColsMobile) return this._fixedColsMobile;
    if (this._fixedCols) return this._fixedCols;
    const avail = this.clientWidth || 600;
    // higher resolution so the (small) mark keeps detail
    return Math.max(90, Math.min(180, Math.round(avail / 6)));
  }
  _rebuild() {
    if (!this._img) return;
    const cols = this._cols();
    // fit the whole field width into the container, then fill the box with rows
    const fontSize = Math.max(3, Math.min(28, (this.clientWidth || 600) / (cols * CHAR_ASPECT)));
    const rows = (this.clientHeight || 300) / fontSize;
    this._pre.style.fontSize = fontSize.toFixed(1) + "px";
    const scale = window.innerWidth < 768 ? this._scaleMobile : this._scale;
    this._mask = buildMask(this._img, cols, rows, scale);
    if (reduceMotion) {
      this._pre.textContent = morphFrame(this._mask, this._ramp, 0, true, this._flow); // static formed frame
      this._frames = null;
    } else {
      this._frames = bakeFrames(this._mask, this._ramp, 48, this._flow);
      this._idx = 0;
    }
  }
  _applyStyle() {
    const light = this._theme === "light";
    const color = this._accent;
    this._pre.style.color = color;
    this._pre.style.textShadow = !this._glow
      ? "none"
      : light
        ? "0 0 4px " + color + "55"
        : "0 0 6px " + color + "88, 0 0 14px " + color + "44";
  }
  _observe() {
    this._io = new IntersectionObserver((entries) => {
      this._inView = entries[0].isIntersecting;
      if (this._inView && document.visibilityState !== "hidden") { if (!this._mask) this._rebuild(); this._start(); }
      else this._stop();
    }, { threshold: 0.05 });
    this._io.observe(this);
  }
  _start() {
    if (this._running || reduceMotion || !this._frames) return;
    this._running = true; requestAnimationFrame(this._raf);
  }
  _stop() { this._running = false; }
  _loop(now) {
    if (!this._running) return;
    requestAnimationFrame(this._raf);
    if (!this._frames) return;
    if (now - this._last < 1000 / this._fps) return;
    this._last = now;
    this._idx = (this._idx + 1) % this._frames.length;
    this._pre.textContent = this._frames[this._idx];
  }
}

if (!customElements.get("moneybird-ascii-logo")) {
  customElements.define("moneybird-ascii-logo", MoneybirdAsciiLogo);
}
